import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { completeDispatch } from '../store/slices/dispatchSlice';
import { AlertTriangle, MapPin, CheckCircle, ChevronDown, Coffee, Car, Send, X, FileText, Plus } from 'lucide-react';
import { cn, getCategoryIcon, getSeverityBg, getSeverityColor, API_BASE_URL } from '../lib/utils';
import { useMobileContext } from '../layouts/MobileLayout';
import NavigationCompass from '../components/shared/NavigationCompass';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4040';

type UnitStatus = 'available' | 'busy' | 'enroute' | 'onscene' | 'onbreak' | 'conveying';

const statusConfig: Record<UnitStatus, { label: string; color: string; dotColor: string; borderStyle: string }> = {
  available: { label: 'Available', color: 'text-emerald-400', dotColor: 'bg-emerald-400 shadow-[0_0_8px_#34d39960]', borderStyle: 'border-emerald-400/20 bg-emerald-400/[0.04]' },
  busy: { label: 'Busy', color: 'text-amber-400', dotColor: 'bg-amber-400 shadow-[0_0_8px_#fbbf2460]', borderStyle: 'border-amber-400/20 bg-amber-400/[0.04]' },
  enroute: { label: 'En Route', color: 'text-cyan-400', dotColor: 'bg-cyan-400 shadow-[0_0_8px_#22d3ee60]', borderStyle: 'border-cyan-400/20 bg-cyan-400/[0.04]' },
  onscene: { label: 'On Scene', color: 'text-red-400', dotColor: 'bg-red-400 shadow-[0_0_8px_#f8717160]', borderStyle: 'border-red-400/20 bg-red-400/[0.04]' },
  onbreak: { label: 'On Break', color: 'text-purple-400', dotColor: 'bg-purple-400 shadow-[0_0_8px_#c084fc60]', borderStyle: 'border-purple-400/20 bg-purple-400/[0.04]' },
  conveying: { label: 'Conveying', color: 'text-orange-400', dotColor: 'bg-orange-400 shadow-[0_0_8px_#fb923c60]', borderStyle: 'border-orange-400/20 bg-orange-400/[0.04]' },
};

interface ChatMsg {
  id: string;
  channel: string;
  channelType: string;
  targetUserId?: string;
  groupId?: string;
  from: string;
  fromName: string;
  text: string;
  timestamp: string;
  eventId?: string;
}

interface ChatChannel {
  id: string;
  type: 'command' | 'private' | 'group';
  name: string;
  targetUserId?: string;
  groupId?: string;
}

export default function MobileDispatchPage() {
  const navigate = useNavigate();
  const reduxDispatch = useAppDispatch();
  const { user, token } = useAppSelector((state) => state.auth);
  const { activeDispatch } = useAppSelector((state) => state.dispatch);
  const { unitStatus, setUnitStatus, chatOpen, setChatOpen, mdtResource } = useMobileContext();
  const [statusOpen, setStatusOpen] = useState(false);
  const [activeChannel, setActiveChannel] = useState<ChatChannel | null>(null);
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [incidentLogs, setIncidentLogs] = useState<any[]>([]);
  const [urgentProgress, setUrgentProgress] = useState(0);
  const [emergencyActivated, setEmergencyActivated] = useState(false);
  const [showConveyingModal, setShowConveyingModal] = useState(false);
  const [conveyingLocation, setConveyingLocation] = useState('');
  const [showClosingModal, setShowClosingModal] = useState(false);
  const [closingRemarks, setClosingRemarks] = useState('');
  const [submittingClose, setSubmittingClose] = useState(false);
  const [showCompass, setShowCompass] = useState(false);
  const pressCountRef = useRef(0);
  const pressTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const acceptedAssignment = activeDispatch?.status === 'accepted' ? activeDispatch : null;

  // Urgent Assistance 3-press handler
  const handleUrgentPress = () => {
    pressCountRef.current++;
    setUrgentProgress((prev) => Math.min(prev + 33.33, 100));

    // Reset press count after 1 second
    if (pressTimeoutRef.current) clearTimeout(pressTimeoutRef.current);
    pressTimeoutRef.current = setTimeout(() => {
      pressCountRef.current = 0;
      setUrgentProgress(0);
    }, 1000);

    // Trigger emergency after 3 presses
    if (pressCountRef.current >= 3) {
      if (pressTimeoutRef.current) clearTimeout(pressTimeoutRef.current);
      pressCountRef.current = 0;
      triggerEmergency();
    }
  };

  // Calculate countdown display
  const getPressDisplay = () => {
    if (pressCountRef.current === 0) return 'Urgent Assist';
    if (pressCountRef.current >= 3) return 'Activated';
    return `${3 - pressCountRef.current}`;
  };


  const triggerEmergency = async () => {
    // Long vibration pattern for emergency
    if (navigator.vibrate) {
      navigator.vibrate([500, 200, 500, 200, 500, 200, 1000]);
    }

    // Play long emergency tone
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 880; // A5
    oscillator.type = 'sine';
    gainNode.gain.value = 0.5;

    oscillator.start();
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 2);
    oscillator.stop(audioContext.currentTime + 2);

    try {
      // Get GPS location
      let location = { latitude: 0, longitude: 0, address: 'Unknown' };
      if (navigator.geolocation) {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
        });

        // Reverse geocode to get address
        try {
          const geocodeRes = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&zoom=18&addressdetails=1`,
            { headers: { 'User-Agent': 'C2-Command-System' } }
          );
          if (geocodeRes.ok) {
            const geocodeData = await geocodeRes.json();
            const addr = geocodeData.address || {};
            const parts = [
              addr.road || addr.street || '',
              addr.suburb || addr.district || '',
              addr.town || addr.city || addr.village || '',
              addr.county || '',
              addr.postcode || ''
            ].filter(Boolean);
            location = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              address: parts.length > 0
                ? `GPS Location Near: ${parts.join(', ')}`
                : `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
            };
          } else {
            location = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
            };
          }
        } catch {
          location = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
          };
        }
      }

      // Get event ID from localStorage
      const eventStr = localStorage.getItem('c2_mdt_event');
      const event = eventStr ? JSON.parse(eventStr) : null;
      const eventId = event?.id;

      // Create emergency incident
      const incidentRes = await fetch(`${API_BASE_URL}/incidents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          eventId,
          title: `EMERGENCY - ${mdtResource?.name || 'Unit'} Urgent Assistance`,
          category: 'medical',
          severity: 'critical',
          status: 'active',
          location,
          description: `Emergency assistance requested by ${mdtResource?.name || 'Unit'}. Location: ${location.address}`,
        }),
      });

      if (incidentRes.ok) {
        const incidentData = await incidentRes.json();

        // Assign resource to incident
        if (mdtResource) {
          await fetch(`${API_BASE_URL}/resources/${mdtResource.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              assignedIncident: incidentData.incident.id,
              status: 'dispatched',
            }),
          });
        }

        // Add incident log
        await fetch(`${API_BASE_URL}/incidents/${incidentData.incident.id}/logs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            details: `${mdtResource?.name || 'Unit'} activated emergency assistance via MDT`,
            action: 'Emergency Activated',
          }),
        });

        // Emit socket event to notify Command
        if (socketRef.current) {
          socketRef.current.emit('emergency:activated', {
            incidentId: incidentData.incident.id,
            resourceId: mdtResource?.id,
            resourceName: mdtResource?.name,
            location,
          });
        }

        setEmergencyActivated(true);
        setTimeout(() => setEmergencyActivated(false), 5000);
      }
    } catch (error) {
      console.error('Emergency activation failed:', error);
    } finally {
      setUrgentProgress(0);
    }
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pressTimeoutRef.current) clearTimeout(pressTimeoutRef.current);
    };
  }, []);
  
  // Determine display status based on assignment and unit status
  const displayStatus = !acceptedAssignment ? 'Available' :
                        unitStatus === 'enroute' ? 'En Route' :
                        unitStatus === 'onscene' ? 'On Scene' : 'Assigned';
  const displayColor = !acceptedAssignment ? 'text-emerald-400' :
                       unitStatus === 'enroute' ? 'text-cyan-400' :
                       unitStatus === 'onscene' ? 'text-red-400' : 'text-amber-400';
  const displayDotColor = !acceptedAssignment ? 'bg-emerald-400 shadow-[0_0_8px_#34d39960]' :
                          unitStatus === 'enroute' ? 'bg-cyan-400 shadow-[0_0_8px_#22d3ee60]' :
                          unitStatus === 'onscene' ? 'bg-red-400 shadow-[0_0_8px_#f8717160]' : 'bg-amber-400 shadow-[0_0_8px_#fbbf2460]';
  const cfg = statusConfig[unitStatus];

  const callSign = mdtResource?.name || user?.badgeNumber || 'A-001';

  // Connect socket for real-time chat
  useEffect(() => {
    if (!user) return;
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token: localStorage.getItem('c2_token'), userId: user.id, username: user.firstName ? `${user.firstName} ${user.lastName}` : user.email },
    });
    socketRef.current = socket;

    socket.on('chat:new_message', (msg: ChatMsg) => {
      setMessages((prev) => [...prev, msg]);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id]);

  // Fetch available channels
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/chat/channels`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setChannels(data.channels || []);
        if (!activeChannel && data.channels?.length > 0) {
          setActiveChannel(data.channels[0]);
        }
      })
      .catch(() => {});
  }, [token]);

  // Fetch messages for active channel
  useEffect(() => {
    if (!activeChannel || !token) return;
    const params = new URLSearchParams();
    params.append('type', activeChannel.type);
    if (activeChannel.type === 'private') params.append('targetUserId', activeChannel.targetUserId || '');
    if (activeChannel.type === 'group') params.append('groupId', activeChannel.groupId || '');

    fetch(`${API_BASE_URL}/chat?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setMessages(data.messages || []))
      .catch(() => {});
  }, [activeChannel, token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeChannel, chatOpen]);

  // Fetch incident logs when assignment is accepted
  useEffect(() => {
    if (!acceptedAssignment || !token) {
      setIncidentLogs([]);
      return;
    }
    fetch(`${API_BASE_URL}/incidents/${acceptedAssignment.incidentId}/logs`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setIncidentLogs(data.logs || []))
      .catch(() => setIncidentLogs([]));
  }, [acceptedAssignment?.incidentId, token]);

  const sendMessage = () => {
    const text = chatInput.trim();
    if (!text || !socketRef.current || !user || !activeChannel) return;
    socketRef.current.emit('chat:message', {
      channelType: activeChannel.type,
      targetUserId: activeChannel.targetUserId,
      groupId: activeChannel.groupId,
      from: user.id,
      fromName: `${user.firstName} ${user.lastName}`,
      text,
    });
    setChatInput('');
  };

  const handleChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleAddNote = async () => {
    if (!noteInput.trim() || !acceptedAssignment || !token) return;
    setAddingNote(true);
    try {
      const res = await fetch(`${API_BASE_URL}/incidents/${acceptedAssignment.incidentId}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ details: noteInput.trim(), action: 'Note' }),
      });
      if (res.ok) {
        setNoteInput('');
        // Refresh logs after adding
        fetch(`${API_BASE_URL}/incidents/${acceptedAssignment.incidentId}/logs`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((data) => setIncidentLogs(data.logs || []))
          .catch(() => {});
      }
    } catch { /* ignore */ } finally {
      setAddingNote(false);
    }
  };

  // ── Chat overlay ──
  if (chatOpen) {
    return (
      <div className="flex flex-col h-full">
        {/* Chat header */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <span className="text-[15px] font-extrabold text-white">Messages</span>
          <button onClick={() => setChatOpen(false)}
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <X className="w-3.5 h-3.5 text-white/50" />
          </button>
        </div>

        {/* Chat tabs */}
        <div className="flex gap-1 px-4 py-2 flex-shrink-0 overflow-x-auto"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
        >
          {channels.map((ch) => {
            return (
              <button key={ch.id} onClick={() => setActiveChannel(ch)}
                className={cn(
                  'px-3.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5',
                  activeChannel?.id === ch.id
                    ? 'bg-blue-500 text-white'
                    : 'text-white/40 hover:text-white/60'
                )}
                style={activeChannel?.id !== ch.id ? { border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.03)' } : {}}
              >
                {ch.name}
              </button>
            );
          })}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5 min-h-0">
          <div className="text-center mb-3">
            <span className="text-[9px] text-white/25 px-3 py-1 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.04)' }}
            >
              Secure channel &middot; {activeChannel?.name || 'channel'} &middot; Today
            </span>
          </div>
          {messages.map((m) => {
            const isMe = m.from === user?.id;
            return (
              <div key={m.id} className={cn('max-w-[80%]', isMe ? 'ml-auto' : 'mr-auto')}>
                <div className={cn(
                  'px-3 py-2 text-[11px] leading-relaxed',
                  isMe
                    ? 'rounded-2xl rounded-br-sm bg-blue-500 text-white'
                    : 'rounded-2xl rounded-bl-sm text-white/90'
                )}
                  style={!isMe ? { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.04)' } : {}}
                >
                  <div className={cn('text-[8px] font-bold mb-1', isMe ? 'text-white/60' : 'text-blue-400')}>
                    {m.fromName}
                  </div>
                  {m.text}
                  <div className={cn('text-[7px] mt-1', isMe ? 'text-white/45' : 'text-white/25')}>
                    {new Date(m.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <div className="flex gap-2 px-4 py-3 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
        >
          <input type="text" placeholder={`Message ${activeChannel?.name || 'channel'}...`}
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleChatKeyDown}
            className="flex-1 px-4 py-2.5 rounded-full text-[12px] text-white/90 placeholder-white/30 outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
          />
          <button onClick={sendMessage}
            className={cn(
              'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-opacity',
              chatInput.trim() ? 'bg-blue-500 opacity-100' : 'bg-blue-500/40 opacity-50'
            )}
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    );
  }

  // ── Main dispatch page ──
  return (
    <div className="flex flex-col h-full">
      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto px-[18px] pb-4 min-h-0 space-y-4">

        {/* ── STATUS DISPLAY ── */}
        <div className="pt-2">
          <button onClick={() => {
            console.log('Status toggle clicked, current statusOpen:', statusOpen);
            setStatusOpen(!statusOpen);
          }}
            className="flex items-center gap-2.5 w-full"
          >
            <div className={cn('w-3 h-3 rounded-full flex-shrink-0 animate-pulse', displayDotColor)} />
            <span className={cn('text-[18px] font-extrabold flex-1 text-left', displayColor)}>
              {displayStatus}
            </span>
            <ChevronDown className={cn('w-4 h-4 text-white/30 transition-transform', statusOpen && 'rotate-180')} />
          </button>

          {/* Call sign */}
          <div className="flex items-center gap-2 mt-1 ml-[22px]">
            <span className="text-[16px] font-extrabold font-mono text-white/90">
              {callSign}
            </span>
            <span className="text-[9px] text-white/30">
              {mdtResource?.department || user?.department || 'Command'} &middot; {user?.firstName} {user?.lastName}
            </span>
          </div>
        </div>

        {/* STATUS SELECTOR (expandable) */}
        {statusOpen && (
          <div className="flex gap-[5px] animate-in slide-in-from-top-2 duration-200">
            {(Object.entries(statusConfig) as [UnitStatus, typeof cfg][]).map(([key, c]) => (
              <button
                key={key}
                onClick={() => {
                  console.log('Status button clicked:', key, c.label);
                  console.log('mdtResource:', mdtResource);
                  console.log('acceptedAssignment:', acceptedAssignment);
                  console.log('socketRef.current:', socketRef.current);
                  setUnitStatus(key);
                  setStatusOpen(false);
                  // Emit status change to backend for logging
                  if (mdtResource && acceptedAssignment) {
                    const payload = {
                      resourceId: mdtResource.id,
                      resourceName: mdtResource.name,
                      incidentId: acceptedAssignment.incidentId,
                      status: key,
                      statusLabel: c.label,
                    };
                    console.log('Emitting mdt:status_change:', payload);
                    socketRef.current?.emit('mdt:status_change', payload);
                  } else {
                    console.log('Cannot emit: missing mdtResource or acceptedAssignment');
                  }
                }}
                className={cn(
                  'flex-1 py-2.5 rounded-[14px] text-center transition-all active:scale-93',
                  unitStatus === key
                    ? cn('border-[1.5px]', c.borderStyle)
                    : 'border-[1.5px] border-white/[0.06] bg-white/[0.02]'
                )}
              >
                <div className={cn('text-[7px] font-bold uppercase tracking-wide mt-0.5',
                  unitStatus === key ? c.color : 'text-white/30'
                )}>
                  {c.label}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* ── ASSIGNED INCIDENT ── */}
        <div>
          <p className="text-[8px] font-bold uppercase tracking-[1.5px] text-white/30 mb-2">
            Assigned Incident
          </p>
          {acceptedAssignment ? (
            <div className="glass-card overflow-hidden">
              {/* Severity bar */}
              <div className="flex items-center justify-between px-3.5 py-[7px]"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
              >
                <span className={cn(
                  'text-[8px] font-extrabold uppercase tracking-wide px-2 py-[3px] rounded-md',
                  getSeverityBg(acceptedAssignment.incidentSeverity),
                  getSeverityColor(acceptedAssignment.incidentSeverity)
                )}>
                  {acceptedAssignment.incidentSeverity}
                </span>
                <span className="text-[9px] font-mono text-white/30">{acceptedAssignment.incidentId}</span>
              </div>
              <div className="p-3.5 space-y-2.5">
                <div className="flex items-start gap-2">
                  <span className="text-xl">{getCategoryIcon(acceptedAssignment.incidentCategory)}</span>
                  <p className="text-[15px] font-bold text-white/90 leading-tight flex-1">
                    {acceptedAssignment.incidentTitle}
                  </p>
                </div>
                <div className="flex items-start gap-2 p-2.5 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.04)' }}
                >
                  <MapPin className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-white/80">
                      {acceptedAssignment.incidentLocation.address || 'Location pending'}
                    </p>
                    <p className="text-[9px] text-white/25 font-mono">
                      {acceptedAssignment.incidentLocation.latitude.toFixed(4)}, {acceptedAssignment.incidentLocation.longitude.toFixed(4)}
                    </p>
                  </div>
                </div>
                {acceptedAssignment.notes && (
                  <p className="text-[10px] text-white/50 p-2 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.04)' }}
                  >
                    {acceptedAssignment.notes}
                  </p>
                )}
                <div className="flex gap-2 pt-1">
                  {unitStatus === 'enroute' ? (
                    <button onClick={() => {
                      setUnitStatus('onscene');
                      if (mdtResource && acceptedAssignment) {
                        socketRef.current?.emit('mdt:status_change', { resourceId: mdtResource.id, resourceName: mdtResource.name, incidentId: acceptedAssignment.incidentId, status: 'onscene', statusLabel: 'On Scene' });
                      }
                    }}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform"
                      style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.15)', color: '#f87171' }}
                    >
                      <MapPin className="w-3.5 h-3.5" /> On Scene
                    </button>
                  ) : unitStatus === 'onscene' ? (
                    <button onClick={() => setShowConveyingModal(true)}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform"
                      style={{ background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.25)', color: '#fb923c' }}
                    >
                      <Car className="w-3.5 h-3.5" /> Conveying
                    </button>
                  ) : unitStatus === 'conveying' ? (
                    <button onClick={() => setShowConveyingModal(true)}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform"
                      style={{ background: 'rgba(251,146,60,0.25)', border: '1px solid rgba(251,146,60,0.4)', color: '#fb923c' }}
                    >
                      <Car className="w-3.5 h-3.5" /> Conveying
                    </button>
                  ) : (
                    <button onClick={() => {
                      setUnitStatus('enroute');
                      if (mdtResource && acceptedAssignment) {
                        socketRef.current?.emit('mdt:status_change', { resourceId: mdtResource.id, resourceName: mdtResource.name, incidentId: acceptedAssignment.incidentId, status: 'enroute', statusLabel: 'En Route' });
                      }
                    }}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform"
                      style={{ background: 'rgba(68,237,170,0.12)', border: '1px solid rgba(68,237,170,0.15)', color: '#34d399' }}
                    >
                      En Route
                    </button>
                  )}
                  <button
                    onClick={() => setShowClosingModal(true)}
                    disabled={unitStatus !== 'onscene' && unitStatus !== 'conveying'}
                    className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-all"
                    style={unitStatus === 'onscene' || unitStatus === 'conveying'
                      ? { background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', color: '#22c55e' }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.25)' }
                    }
                  >
                    <CheckCircle className="w-3 h-3" /> Done
                  </button>
                </div>

                {/* Closing remarks modal */}
                {showClosingModal && (
                  <div className="fixed inset-0 z-[200] flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
                    <div className="w-full max-w-md rounded-t-2xl p-5 space-y-4" style={{ background: '#1a1d23', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center justify-between">
                        <p className="text-[15px] font-extrabold text-white">Closing Remarks</p>
                        <button onClick={() => setShowClosingModal(false)}>
                          <X className="w-4 h-4 text-white/40" />
                        </button>
                      </div>
                      <p className="text-[11px] text-white/40">Add a handover note for the command team. This will be posted to the incident log before closing your assignment.</p>
                      <textarea
                        autoFocus
                        rows={4}
                        placeholder="e.g. Patient conveyed to Kings College, condition stable. Scene clear."
                        value={closingRemarks}
                        onChange={(e) => setClosingRemarks(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl text-sm text-white/90 placeholder-white/25 outline-none resize-none"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                      />
                      <button
                        disabled={submittingClose}
                        onClick={async () => {
                          if (!acceptedAssignment) return;
                          setSubmittingClose(true);
                          try {
                            if (closingRemarks.trim()) {
                              await fetch(`${API_BASE_URL}/incidents/${acceptedAssignment.incidentId}/logs`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ details: `[MDT Closing Remarks] ${closingRemarks.trim()}`, action: 'Closing Remarks' }),
                              });
                            }
                          } catch { /* ignore */ } finally {
                            setSubmittingClose(false);
                            setShowClosingModal(false);
                            setClosingRemarks('');
                            reduxDispatch(completeDispatch(acceptedAssignment.id));
                            setUnitStatus('available');
                          }
                        }}
                        className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-95"
                        style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', color: '#22c55e' }}
                      >
                        {submittingClose ? 'Submitting...' : 'Complete Job'}
                      </button>
                      <button
                        onClick={() => setShowClosingModal(false)}
                        className="w-full py-2.5 rounded-xl text-xs font-medium"
                        style={{ color: 'rgba(255,255,255,0.3)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Conveying location modal */}
                {showConveyingModal && (
                  <div className="fixed inset-0 z-[200] flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
                    <div className="w-full max-w-md rounded-t-2xl p-5 space-y-4" style={{ background: '#1a1d23', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center justify-between">
                        <p className="text-[15px] font-extrabold text-white">Conveying To</p>
                        <button onClick={() => setShowConveyingModal(false)}>
                          <X className="w-4 h-4 text-white/40" />
                        </button>
                      </div>
                      <p className="text-[11px] text-white/40">Enter the hospital or location you are conveying the patient to</p>
                      <input
                        autoFocus
                        type="text"
                        placeholder="e.g. Kings College Hospital"
                        value={conveyingLocation}
                        onChange={(e) => setConveyingLocation(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl text-sm text-white/90 placeholder-white/25 outline-none"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && conveyingLocation.trim()) {
                            setUnitStatus('conveying');
                            if (mdtResource && acceptedAssignment) {
                              socketRef.current?.emit('mdt:status_change', { resourceId: mdtResource.id, resourceName: mdtResource.name, incidentId: acceptedAssignment.incidentId, status: 'conveying', statusLabel: `Conveying to ${conveyingLocation.trim()}` });
                            }
                            setShowConveyingModal(false);
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          if (!conveyingLocation.trim()) return;
                          setUnitStatus('conveying');
                          if (mdtResource && acceptedAssignment) {
                            socketRef.current?.emit('mdt:status_change', { resourceId: mdtResource.id, resourceName: mdtResource.name, incidentId: acceptedAssignment.incidentId, status: 'conveying', statusLabel: `Conveying to ${conveyingLocation.trim()}` });
                          }
                          setShowConveyingModal(false);
                        }}
                        disabled={!conveyingLocation.trim()}
                        className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-95"
                        style={conveyingLocation.trim()
                          ? { background: 'rgba(251,146,60,0.25)', border: '1px solid rgba(251,146,60,0.4)', color: '#fb923c' }
                          : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.2)' }
                        }
                      >
                        Confirm Conveying
                      </button>
                    </div>
                  </div>
                )}
                {/* Incident logs/notes */}
                {incidentLogs.length > 0 && (
                  <div className="pt-2">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-white/30 mb-2">Incident Notes</p>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                      {incidentLogs.map((log) => (
                        <div key={log.id} className="p-2 rounded-lg text-[10px]"
                          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-white/70">{log.action}</span>
                            <span className="text-white/25">
                              {new Date(log.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </span>
                          </div>
                          <p className="text-white/50">{log.details}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Notes input */}
                <div className="pt-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add note..."
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNote(); } }}
                      className="flex-1 px-3 py-2 rounded-lg text-[11px] text-white/90 placeholder-white/30 outline-none"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                    />
                    <button
                      onClick={handleAddNote}
                      disabled={addingNote || !noteInput.trim()}
                      className="px-3 py-2 rounded-lg flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
                      style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.2)', color: '#60a5fa' }}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="glass-card p-5">
              <div className="text-center text-white/30">
                <span className="text-2xl block mb-1 opacity-30">📋</span>
                <span className="text-[11px]">No active assignment</span>
                <br />
                <span className="text-[10px] text-white/15">Standing by for dispatch</span>
              </div>
            </div>
          )}
        </div>

        {/* ── ACTIONS ── */}
        <div>
          <p className="text-[8px] font-bold uppercase tracking-[1.5px] text-white/30 mb-2">
            Actions
          </p>
          <div className="grid grid-cols-2 gap-2">
            {/* Urgent Assistance */}
            <button
              onClick={handleUrgentPress}
              className="py-4 rounded-[16px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform relative overflow-hidden"
              style={{ background: 'rgba(255,80,116,0.06)', border: '1.5px solid rgba(255,80,116,0.2)' }}
            >
              {/* Progress bar overlay */}
              {urgentProgress > 0 && (
                <div
                  className="absolute inset-0 bg-red-500/30 transition-all duration-100"
                  style={{ width: `${urgentProgress}%` }}
                />
              )}
              <AlertTriangle className="w-6 h-6 text-red-400 relative z-10" />
              <span className="text-[9px] font-bold text-red-400 uppercase tracking-wide relative z-10">
                {getPressDisplay()}
              </span>
            </button>
            {/* Request Break */}
            <button
              onClick={() => setUnitStatus('onbreak')}
              className="py-4 rounded-[16px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
              style={{ background: 'rgba(184,140,255,0.04)', border: '1.5px solid rgba(184,140,255,0.15)' }}
            >
              <Coffee className="w-6 h-6 text-purple-400" />
              <span className="text-[9px] font-bold text-purple-400 uppercase tracking-wide">Request Break</span>
            </button>
            {/* Patient Report Form */}
            <button
              onClick={() => navigate('/prf')}
              className="col-span-2 py-4 rounded-[16px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
              style={{ background: 'rgba(34,197,94,0.06)', border: '1.5px solid rgba(34,197,94,0.2)' }}
            >
              <FileText className="w-6 h-6 text-green-400" />
              <span className="text-[9px] font-bold text-green-400 uppercase tracking-wide">Patient Report Form</span>
            </button>
          </div>
        </div>


      </div>

      {/* Navigation Compass Modal */}
      {showCompass && acceptedAssignment?.incidentLocation?.latitude && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <NavigationCompass
            targetLat={acceptedAssignment.incidentLocation.latitude}
            targetLng={acceptedAssignment.incidentLocation.longitude}
            targetName={acceptedAssignment.incidentTitle}
            onClose={() => setShowCompass(false)}
            className="w-full max-w-sm"
          />
        </div>
      )}

      {/* Emergency Activation Overlay */}
      {emergencyActivated && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-red-500/90 backdrop-blur-md animate-in fade-in duration-300">
          <div className="text-center">
            <AlertTriangle className="w-20 h-20 text-white mx-auto mb-4 animate-pulse" />
            <h2 className="text-3xl font-extrabold text-white mb-2">EMERGENCY ACTIVATED</h2>
            <p className="text-white/80 text-sm">Incident created and resource assigned</p>
            <p className="text-white/60 text-xs mt-2">Command has been notified</p>
          </div>
        </div>
      )}
    </div>
  );
}
